import { DecimalPipe, LowerCasePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, DOCUMENT, DestroyRef, ElementRef, Input, OnInit, ViewEncapsulation, booleanAttribute, computed, effect, inject, numberAttribute, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { APP_CONSTANTS, CURRENCY, CUSTOM_PLAN_CONFERENCE_TYPE, CUSTOM_PLAN_GROUP_APPOINTMENT_TYPE, CUSTOM_PLAN_STORE_TYPE, CUSTOM_PLAN_USER_TYPE, DECIMAL_DIGITS, DECIMAL_LOCALE, INTERVAL, LANGUAGES, PRICING_PLAN_PRECEDENCE, RENDER_MODE, SUBSCRIPTION, SUBSCRIPTION_IDS, SUPPORTED_LANGUAGES } from '@constants/app.constants';
import { getExtraCost, MAX_LIMIT, MIN_CONFERENCE_LIMIT, MIN_LIMIT, SALES_TOKEN } from '@constants/pricing.constants';
import { resolvePricingList } from '@constants/pricing-resolver';
import { V2_ADDONS, AddOnDetails } from '@constants/v2/add-ons.constants';
import { V2_COMPARISON, ComparisonCategory, ComparisonCell } from '@constants/v2/comparison.constants';
import { Currency, IntervalType, PricingDetails, RenderModeType, SubscriptionType, SupportedLanguages } from '@db-models/pricing-db.model';
import { SubscriptionModel } from '@db-models/subscribe-db.model';
import { MaxInputValueDirective } from '@directives/max-input-value.directive';
import { environment } from '@env/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalioSafePipe } from '@pipes/calio-safe.pipe';
import { HelperService } from '@services/util-service/helper.service';
import { LoggerService } from '@services/util-service/logger.service';

const directives = [MaxInputValueDirective];
const modules = [TranslateModule, NgClass, NgTemplateOutlet, FormsModule];
const pipes = [LowerCasePipe, DecimalPipe, CalioSafePipe];

@Component({
  selector: 'calenso-pricing-table',
  templateUrl: './calenso-pricing-table.component.html',
  styleUrl: './calenso-pricing-table.component.scss',
  standalone: true,
  imports: [...modules, ...pipes, ...directives],
  encapsulation: ViewEncapsulation.ShadowDom
})
export class CalensoPricingTableComponent implements OnInit {

  @Input('language') set lang(language: SupportedLanguages) {
    this.setLanguage(language);
  };

  @Input('render-mode') set RenderMode(renderMode: RenderModeType) {
    this._renderModeSignal.set(Object.values(RENDER_MODE).includes(renderMode) ? renderMode : RENDER_MODE.DASHBOARD);
    this.setPricingJson();
  };

  @Input('currency') set Currency(currency: Currency) {
    this.setCurrency(currency);
  };

  @Input('current-subscription') set setCurrentSubscription(currentSubscription: 'undefined' | 'null') {
    if (this._renderModeSignal() === RENDER_MODE.DASHBOARD && currentSubscription && (currentSubscription !== "undefined") && (currentSubscription !== "null")) {
      this._currentSubscriptionSignal.set(JSON.parse(currentSubscription) as SubscriptionModel);
      // show currenctly active plan with interval
      this._intervalSignal.set(this._currentSubscriptionSignal()?.subscription_interval);
      this.currentPlanPrecedenceSignal.set(PRICING_PLAN_PRECEDENCE[this._currentSubscriptionSignal()?.subscription_type_id as keyof typeof PRICING_PLAN_PRECEDENCE]);
      this.setCurrentSubscriptionChanges();
      this.isPartnerInTrialModeSignal.set(!!this._currentSubscriptionSignal()?.is_trial);
    }
  };

  @Input('subscription') set setSubscription(preSelectedSubscription: SubscriptionType) {
    if (this._renderModeSignal() === RENDER_MODE.DASHBOARD && Object.values(SUBSCRIPTION).includes(preSelectedSubscription)) {
      this._preSelectedSubscriptionSignal.set(preSelectedSubscription);
      this._preSelectedIntervalSignal() && this.setPreDefineSelectedPlan();
    }
  };

  @Input('interval') set setIntervals(preSelectedInterval: IntervalType) {
    if (this._renderModeSignal() === RENDER_MODE.DASHBOARD && Object.values(INTERVAL).includes(preSelectedInterval)) {
      this._preSelectedIntervalSignal.set(preSelectedInterval);
      this._preSelectedSubscriptionSignal() && this.setPreDefineSelectedPlan();
    }
  };

  @Input('popular') set setPopularPlan(popularPlan: SubscriptionType) {
    this.setPopular(popularPlan);
  };

  @Input('show-custom-plan-section') set setCustomPlanSection(showCustomPlan: string) {
    (showCustomPlan === 'true') && (this._showCustomPlanSectionSignal.set(true));
  };

  @Input('token') salesToken: string;
  @Input({ alias: 'minimum-subscription', transform: numberAttribute }) set minimumSubscription(_minimumSubscription: number) {
    Object.keys(PRICING_PLAN_PRECEDENCE).map(Number).includes(_minimumSubscription) && (this._minimumSubscriptionSignal.set(_minimumSubscription));
    this.setPricingJson();
  };

  @Input({ alias: 'hide-monthly-interval', transform: booleanAttribute }) set hideMonthlyInterval(_hideMonthlyInterval: boolean) {
    (_hideMonthlyInterval === true) && (this._hideMonthlyIntervalSignal.set(true), this.setInterval(this.yearly));
  };

  /**
   * Comma-separated list of FEATURE FLAG names the current partner has active
   * (e.g. `is_analytics_enabled,is_timezone_enabled,is_hubspot_enabled`). Add-on
   * cards whose `featureFlag` matches an entry in this list swap their price +
   * "Contact support" CTA for an "Active on your plan" label, so partners
   * don't request enablement of something they already have.
   *
   * Why feature-flag names and not add-on keys: pricing is the source of truth
   * for the addon → featureFlag mapping (see {@link AddOnDetails}). The embedder
   * (dashboard) just sends "here are the flags that are true" — pricing does
   * the rest of the matching internally, so adding a new addon is a one-edit
   * change in `V2_ADDONS` here with no embedder code change.
   *
   * Add-ons whose mechanism is `permission` or `role` (not feature) carry no
   * `featureFlag` and therefore never match an entry in this list — they keep
   * their default "Contact support" CTA until a parallel `active-permissions`
   * / `active-roles` input lands.
   *
   * Example: `<calenso-pricing-table active-features="is_analytics_enabled,is_timezone_enabled" />`
   */
  @Input('active-features') set setActiveFeatures(activeFeatures: string) {
    if (typeof activeFeatures !== 'string' || !activeFeatures.trim()) {
      this._activeFeaturesSignal.set(new Set());
      return;
    }
    const flags = activeFeatures.split(',').map(s => s.trim()).filter(Boolean);
    this._activeFeaturesSignal.set(new Set(flags));
  };

  /**
   * Colour theme: `'auto'` (default) | `'dark'` | `'light'`.
   * Absent/invalid/`'auto'` → follow (and live-track) `prefers-color-scheme`;
   * `'dark'`/`'light'` force it. Toggles `.dark` on the host (see the constructor
   * effect) → activates the `:host(.dark)` tokens. `'auto'` is the right default for
   * standalone / partner-website embeds; the dashboard embed always passes an
   * explicit `theme=` (its `:root.dark` cascade can't cross the Shadow DOM).
   */
  @Input('theme') set theme(value: string) {
    this.setTheme(value);
  };

  private helperService = inject(HelperService);
  private document = inject(DOCUMENT);
  private translate = inject(TranslateService);
  private elementRef = inject(ElementRef<HTMLElement>);
  private destroyRef = inject(DestroyRef);

  protected readonly deployUrl = environment.deployUrl
  protected readonly monthly = INTERVAL.MONTHLY;
  protected readonly yearly = INTERVAL.YEARLY;
  protected readonly dashboard = RENDER_MODE.DASHBOARD;
  protected readonly sales = RENDER_MODE.SALES;
  protected readonly eur = CURRENCY.EUR;
  protected readonly chf = CURRENCY.CHF;
  protected readonly usd = CURRENCY.USD;
  protected readonly gbp = CURRENCY.GBP;
  protected readonly en_US = SUPPORTED_LANGUAGES.EN_US;
  protected readonly de_CH = SUPPORTED_LANGUAGES.DE_CH;
  protected readonly maxLimit = MAX_LIMIT;
  protected readonly minLimit = MIN_LIMIT;
  protected readonly minConferenceLimit = MIN_CONFERENCE_LIMIT;
  // Brand- + currency-aware unit costs. Getters re-resolve on each access,
  // so when _currencySignal() changes (IP detection, user selection) the
  // template's transitive read via currencySymbolSignal() re-runs and these
  // resolve to the new currency. Falls back to EUR if currency is undefined
  // (mirrors the EUR fallback in computeCustomPlanTotal).
  protected get extraUserCost(): number {
    return getExtraCost('user', this._currencySignal() ?? 'EUR', environment.brandName);
  }
  protected get extraGroupAppointmentCost(): number {
    return getExtraCost('group_appointment', this._currencySignal() ?? 'EUR', environment.brandName);
  }
  protected get extraStoreCost(): number {
    return getExtraCost('store', this._currencySignal() ?? 'EUR', environment.brandName);
  }
  protected get extraConferenceCost(): number {
    return getExtraCost('conference', this._currencySignal() ?? 'EUR', environment.brandName);
  }
  protected readonly customPlanUserType = CUSTOM_PLAN_USER_TYPE;
  protected readonly customPlanStoreType = CUSTOM_PLAN_STORE_TYPE;
  protected readonly customPlanGroupAppointmentType = CUSTOM_PLAN_GROUP_APPOINTMENT_TYPE;
  protected readonly customPlanConferenceType = CUSTOM_PLAN_CONFERENCE_TYPE;
  protected readonly decimalDigits = DECIMAL_DIGITS;
  protected readonly decimalLocal = DECIMAL_LOCALE;
  protected readonly currentYear = new Date().getFullYear();
  protected readonly pricingPlanPrecedence = PRICING_PLAN_PRECEDENCE;
  protected readonly subscriptionIds = SUBSCRIPTION_IDS;
  protected readonly enterpriseId = SUBSCRIPTION_IDS.enterprise;
  protected readonly addOns: ReadonlyArray<AddOnDetails> = V2_ADDONS;
  protected readonly compareCategories: ReadonlyArray<ComparisonCategory> = V2_COMPARISON;
  protected showCompareSignal = signal<boolean>(false);

  protected addOnContactHref(): string {
    return `mailto:${environment.supportEmail}?subject=${encodeURIComponent('Add-on enablement request')}`;
  }

  // Enterprise has no self-serve upgrade path — the dashboard CTA opens a
  // pre-filled mailto to support instead of dispatching pricingTablePlanSelectEvent.
  protected enterpriseContactHref(): string {
    return `mailto:${environment.supportEmail}?subject=${encodeURIComponent('Enterprise plan inquiry')}`;
  }

  // True when this plan can't even be requested through the standard
  // upgrade flow — Enterprise routes straight to support email.
  protected isEnterprisePlan(plan: PricingDetails): boolean {
    return plan?.id === SUBSCRIPTION_IDS.enterprise;
  }

  protected toggleCompare(): void {
    const opening = !this.showCompareSignal();
    this.showCompareSignal.update(v => !v);
    // Preserve the public 'pricingTableCompareFeatureEvent' for embedders
    // (dashboard etc.) that listen for it. Fires on open only.
    if (opening) {
      this.compareFeatureList();
    }
  }

  /** Cell type guards for the template — keeps the @switch in the HTML tidy. */
  protected isCellBool(c: ComparisonCell): c is boolean { return typeof c === 'boolean'; }
  protected isCellAddon(c: ComparisonCell): c is { addon: true } { return typeof c === 'object' && c !== null && 'addon' in c; }
  protected isCellText(c: ComparisonCell): c is string { return typeof c === 'string'; }

  // True when the plan has a concrete price for the current currency + interval.
  // Drives whether to render `EUR 35 / month` vs the "Auf Anfrage" fallback.
  // Enterprise (price = 0) shows "Auf Anfrage"; Basic/Starter/Team all have
  // real Stripe prices and render the amount.
  protected hasVisiblePrice(plan: PricingDetails): boolean {
    const cur = this._currencySignal()?.toLowerCase() as Lowercase<Currency> | undefined;
    const interval = this._intervalSignal();
    if (!cur || !interval) return false;
    const price = plan?.[cur]?.[interval]?.original_price;
    return typeof price === 'number' && price > 0;
  }
  protected readonly brandParams = {
    brandName: environment.brandName,
    websiteUrl: environment.websiteUrl,
  };

  protected pricingListSignal = signal<PricingDetails[]>([]);
  protected currentPlanPrecedenceSignal = signal(0);
  protected isCustomPlanSelectionEnabledSignal = signal(false);
  protected customPlanUserSignal = signal(this.minLimit);
  protected customPlanGroupAppointmentSignal = signal(this.minLimit);
  protected customPlanStoreSignal = signal(this.minLimit);
  protected customPlanConferenceSignal = signal(this.minConferenceLimit);
  protected customSelectedPlanSignal = signal<PricingDetails | undefined>(undefined);
  protected appliedPlanSignal = signal<PricingDetails | undefined>(undefined);
  protected customPlanTotalAmountSignal = signal(0);
  protected currencySymbolSignal = signal('');
  protected isPartnerInTrialModeSignal = signal(false);
  protected availablePlansSignal = signal([]);

  protected _languageSignal = signal<SupportedLanguages>(undefined);
  protected _currencySignal = signal<Currency>(undefined);
  protected _renderModeSignal = signal<RenderModeType>(RENDER_MODE.DASHBOARD);
  protected _preSelectedSubscriptionSignal = signal<SubscriptionType>(undefined);
  protected _currentSubscriptionSignal = signal<SubscriptionModel>(undefined);
  protected _intervalSignal = signal<IntervalType>(INTERVAL.YEARLY);
  protected _preSelectedIntervalSignal = signal<IntervalType>(undefined);
  protected _popularPlanSignal = signal<SubscriptionType>(undefined);
  protected _showCustomPlanSectionSignal = signal<boolean>(false);
  protected _minimumSubscriptionSignal = signal<number>(undefined);
  protected _hideMonthlyIntervalSignal = signal<boolean>(false);
  // Set of FEATURE FLAG names the current partner has active (from
  // `active-features` input). An add-on card switches to its "active" state
  // when its `addon.featureFlag` (if any) is in this set. Empty by default;
  // cards stay in the standard "Contact support" CTA state when not matched.
  protected _activeFeaturesSignal = signal<ReadonlySet<string>>(new Set());

  // Theme from the `theme` input. DEFAULT = 'auto' (follow prefers-color-scheme,
  // live-tracked) — the right default for standalone / partner-website embeds. The
  // dashboard embed is unaffected: it always passes an explicit theme= ('light' on
  // the no-dark dashboard, 'dark'/'light' from ThemeService in the themed build).
  protected _themeSignal = signal<'dark' | 'light' | 'auto'>('auto');
  // Live OS/browser preference, seeded + tracked from prefers-color-scheme.
  protected _systemPrefersDarkSignal = signal<boolean>(false);
  // Resolved theme = forced dark/light, or (only when 'auto') the browser preference.
  protected resolvedThemeSignal = computed<'dark' | 'light'>(() => {
    const theme = this._themeSignal();
    return theme === 'auto' ? (this._systemPrefersDarkSignal() ? 'dark' : 'light') : theme;
  });

  constructor() {
    this.setPricingJson();
    this.setupThemeReactivity();
  }

  /**
   * Wire the browser-preference tracking and the host `.dark` class toggle.
   * The media-query listener only changes the *system* signal — when an explicit
   * `theme` input is set it overrides via resolvedThemeSignal, so OS changes are
   * inert until the input is cleared. The listener is torn down on destroy.
   */
  private setupThemeReactivity(): void {
    const mql = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : undefined;
    if (mql) {
      this._systemPrefersDarkSignal.set(mql.matches);
      const onChange = (e: MediaQueryListEvent): void => this._systemPrefersDarkSignal.set(e.matches);
      mql.addEventListener('change', onChange);
      this.destroyRef.onDestroy(() => mql.removeEventListener('change', onChange));
    }

    effect(() => {
      const isDark = this.resolvedThemeSignal() === 'dark';
      this.elementRef.nativeElement.classList.toggle('dark', isDark);
    });
  }

  protected setTheme(value?: string): void {
    // 'dark'/'light' force the theme; anything else (unset/invalid/empty/'auto')
    // → 'auto', which follows prefers-color-scheme.
    this._themeSignal.set(value === 'dark' ? 'dark' : value === 'light' ? 'light' : 'auto');
  }

  ngOnInit(): void {
    this.checkForSalesMode();

    LoggerService.log("[Pricing Page Inputs]", {
      "language": this._languageSignal(),
      "currency": this._currencySignal(),
      "renderMode": this._renderModeSignal(),
      "preSelectedSubscription": this._preSelectedSubscriptionSignal(),
      "currentSubscription": this._currentSubscriptionSignal(),
      "interval": this._intervalSignal(),
      "pricingList": this.pricingListSignal(),
      "token": this.salesToken,
    });

    this.validateInputs();
    this.pricingTableProjectLoaded();

    // Default custom plan is enabled then calculate price
    this.calculateCustomPlanPrice();
  }

  protected setLanguage(language?: SupportedLanguages): void {
    if ([SUPPORTED_LANGUAGES.DE_CH, SUPPORTED_LANGUAGES.EN_US].includes(language)) {
      this.translate.setFallbackLang(language);
    } else {
      const browserLang = this.translate.getBrowserCultureLang();
      if (browserLang && LANGUAGES.find(language => language.locale === browserLang.replace('-', '_'))) {
        this.translate.setFallbackLang(browserLang.replace('-', '_'));
      } else {
        this.translate.setFallbackLang(APP_CONSTANTS.DEFAULT_LANG);
      }
    }
    this._languageSignal.set(this.translate.getFallbackLang() as (SupportedLanguages));
  }

  protected setCurrency(currency?: Currency): void {
    if ([CURRENCY.CHF, CURRENCY.EUR, CURRENCY.USD, CURRENCY.GBP].includes(currency)) {
      this._currencySignal.set(currency);
      this.currencySymbolSignal.set(currency);
    } else {
      this._currencySignal.set(CURRENCY.EUR);
      this.currencySymbolSignal.set(CURRENCY.EUR);
    }
  }

  private setInterval(interval: IntervalType = INTERVAL.YEARLY): void {
    if ([INTERVAL.YEARLY, INTERVAL.MONTHLY].includes(interval)) {
      this._intervalSignal.set(interval);
    }
  }

  private setCurrentSubscriptionChanges(): void {
    this.setInterval(this._currentSubscriptionSignal()?.subscription_interval);
    (this._currentSubscriptionSignal()?.additional_users) && (this.changeUserProfile(this._currentSubscriptionSignal()?.additional_users, this.customPlanUserType));
    (this._currentSubscriptionSignal()?.additional_stores) && (this.changeUserProfile(this._currentSubscriptionSignal()?.additional_stores, this.customPlanStoreType));
    (this._currentSubscriptionSignal()?.additional_events) && (this.changeUserProfile(this._currentSubscriptionSignal()?.additional_events, this.customPlanGroupAppointmentType));
    (this._currentSubscriptionSignal()?.additional_conferences) && (this.changeUserProfile(this._currentSubscriptionSignal()?.additional_conferences, this.customPlanConferenceType));
  }

  private setPreDefineSelectedPlan(): void {
    if (this._preSelectedSubscriptionSignal() === SUBSCRIPTION.ENTERPRISE) {
      LoggerService.warn("Not able to upgrade to ENTERPRISE plan form UI, Need to contact support.");
      return;
    }

    const targetPlanId = this.subscriptionIds[this._preSelectedSubscriptionSignal() as keyof typeof SUBSCRIPTION_IDS];
    const targetPrecedence = this.pricingPlanPrecedence[targetPlanId as keyof typeof PRICING_PLAN_PRECEDENCE];

    if (this.currentPlanPrecedenceSignal() === targetPrecedence) {
      if (this._currentSubscriptionSignal()?.subscription_interval === this.monthly && this._preSelectedIntervalSignal() === this.monthly) {
        LoggerService.warn("Current selected plan is already activated");
      } else if (this._currentSubscriptionSignal()?.subscription_interval === this.monthly && this._preSelectedIntervalSignal() === this.yearly) {
        LoggerService.log("Current plan is interval upgrade");
        const plan = this.pricingListSignal().find(planDetails => planDetails.name.toUpperCase() === this._preSelectedSubscriptionSignal()?.toUpperCase());
        this.submit(plan, this._preSelectedIntervalSignal(), this._currencySignal());
      }
    } else if (this.currentPlanPrecedenceSignal() < targetPrecedence) {
      LoggerService.log("Upgrade plan");
      const plan = this.pricingListSignal().find(planDetails => planDetails.name.toUpperCase() === this._preSelectedSubscriptionSignal()?.toUpperCase());
      this.submit(plan, this._preSelectedIntervalSignal(), this._currencySignal());
    }
  }

  private setPopular(popularPlan: SubscriptionType): void {
    if (Object.values(SUBSCRIPTION).includes(popularPlan)) {
      this._popularPlanSignal.set(popularPlan);
    }
  }

  private validateInputs(): void {
    !this._languageSignal() && this.setLanguage();
    !this._currencySignal() && this.getCurrentFromIp();
    !this._intervalSignal() && this.setInterval();
    if (!this._popularPlanSignal()) {
      this._popularPlanSignal.set(SUBSCRIPTION.TEAM);
    }
  }

  private checkForSalesMode(): void {
    // Standalone preview = canonical deploy URL OR localhost (dev/docker).
    // When embedded inside another app (e.g. dashboard) hostname won't match
    // either, so URL params remain inert as intended.
    const href = window.location.href;
    const isStandalonePreview =
      href.includes(this.deployUrl) || /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/)/.test(href);
    const paramsObject = isStandalonePreview
      ? this.helperService.getParamsFromUrl(new URL(href))
      : undefined;

    // Non-privileged URL params apply in ANY mode — useful for QA / preview /
    // link-sharing scenarios. Embedders that don't want this can host the
    // pricing component on a non-deployUrl domain (paramsObject will be undefined).
    // Supported: ?language=en_US | ?currency=CHF | ?interval=yearly | ?popular=team
    //          | ?theme=auto|dark|light | ?show-custom-plan-section=true   (mirrors the @Input)
    if (paramsObject?.language) this.setLanguage(paramsObject.language);
    if (paramsObject?.currency) this.setCurrency(paramsObject.currency);
    if (paramsObject?.interval) this.setInterval(paramsObject.interval);
    if (paramsObject?.popular) this.setPopular(paramsObject.popular);
    if (paramsObject?.theme) this.setTheme(paramsObject.theme);
    if (paramsObject?.['show-custom-plan-section'] === 'true') {
      this._showCustomPlanSectionSignal.set(true);
    }

    // Sales-mode upgrade requires both ?mode=sales and a valid ?token=SALES_TOKEN.
    if (paramsObject?.mode === this.sales && paramsObject?.token === SALES_TOKEN) {
      this._renderModeSignal.set(RENDER_MODE.SALES);
      this._showCustomPlanSectionSignal.set(true);
    } else if (this._renderModeSignal() === this.sales && this.salesToken === SALES_TOKEN) {
      // Sales mode set via attribute + valid token input
      this._showCustomPlanSectionSignal.set(true);
    }
    // Otherwise: keep whatever render-mode was set via attribute (default DASHBOARD).

    // Sales mode = calculator is the headline section, not a hidden tool.
    // Auto-open it so the recommended-tier total + "you also unlock with X"
    // benefits panel are visible without the user clicking "Manage user
    // profile" first. Dashboard mode keeps the click-to-expand behaviour.
    if (this._renderModeSignal() === this.sales) {
      this.isCustomPlanSelectionEnabledSignal.set(true);
    }

    this.setPricingJson();
  }

  protected toggleInterval(toggle: IntervalType): void {
    this._intervalSignal.set(toggle);
    this.calculateCustomPlanPrice();
  }

  protected submit(priceModel: PricingDetails, interval: IntervalType, currency: Currency): void {
    LoggerService.log("[Info] Selected plan", priceModel);
    LoggerService.log("[Info] Selected interval", interval);
    LoggerService.log("[Info] Selected currency", currency);
    let customPlan = {};
    if (priceModel?.min_workers < this.customPlanUserSignal()) {
      customPlan[this.customPlanUserType] = this.customPlanUserSignal() - priceModel.min_workers;
    }
    if (priceModel?.min_store < this.customPlanStoreSignal()) {
      customPlan[this.customPlanStoreType] = this.customPlanStoreSignal() - priceModel.min_store;
    }
    if (priceModel?.min_group_appointment < this.customPlanGroupAppointmentSignal()) {
      customPlan[this.customPlanGroupAppointmentType] = this.customPlanGroupAppointmentSignal() - priceModel.min_group_appointment;
    }
    if (priceModel?.min_conference < this.customPlanConferenceSignal()) {
      customPlan[this.customPlanConferenceType] = this.customPlanConferenceSignal() - priceModel.min_conference;
    }
    LoggerService.log("[Info] Selected customPlan", customPlan);
    const customEvent = new CustomEvent('pricingTablePlanSelectEvent', { detail: { priceModel, interval, currency, customPlan } });
    this.document.dispatchEvent(customEvent);
  }

  protected compareFeatureList(): void {
    const customEvent = new CustomEvent('pricingTableCompareFeatureEvent');
    this.document.dispatchEvent(customEvent);
  }

  private pricingTableProjectLoaded(): void {
    const customEvent = new CustomEvent('pricingTableLoadedEvent');
    this.document.dispatchEvent(customEvent);
  }

  protected toggleUserProfile(): void {
    this.isCustomPlanSelectionEnabledSignal.update(prev => !prev);
  }

  protected changeUserProfile(customNumber: number, flag: string): void {
    if (flag === this.customPlanUserType) {
      (customNumber < this.minLimit) && (this.customPlanUserSignal.set(this.minLimit));
    } else if (flag === this.customPlanGroupAppointmentType) {
      (customNumber < this.minLimit) && (this.customPlanGroupAppointmentSignal.set(this.minLimit));
    } else if (flag === this.customPlanStoreType) {
      (this.customPlanStoreSignal() < this.minLimit) && (this.customPlanStoreSignal.set(this.minLimit));
    } else if (flag === this.customPlanConferenceType) {
      if (customNumber === 0) {
        this.customPlanConferenceSignal.set(0);
      } else if (customNumber < 5) {
        this.customPlanConferenceSignal.set(5);
      }
    }
    this.calculateCustomPlanPrice();
  }

  private applyCustomProfileToPricingTable(): void {
    this.appliedPlanSignal.set(this.customSelectedPlanSignal());
  }

  protected decrement(flag: string): void {
    if (flag === this.customPlanUserType) {
      (this.customPlanUserSignal() >= this.minLimit) && this.customPlanUserSignal.update(prev => prev - 1);
    } else if (flag === this.customPlanGroupAppointmentType) {
      (this.customPlanGroupAppointmentSignal() >= this.minLimit) && this.customPlanGroupAppointmentSignal.update(prev => prev - 1);
    } else if (flag === this.customPlanStoreType) {
      (this.customPlanStoreSignal() >= this.minLimit) && this.customPlanStoreSignal.update(prev => prev - 1);
    } else if (flag === this.customPlanConferenceType) {
      if (this.customPlanConferenceSignal() === 5) {
        this.customPlanConferenceSignal.set(0);
      } else if (this.customPlanConferenceSignal() >= this.minLimit) {
        this.customPlanConferenceSignal.update(prev => prev - 1);
      }
    }
    this.calculateCustomPlanPrice();
  }

  protected increment(flag: string): void {
    if (flag === this.customPlanUserType) {
      (this.customPlanUserSignal() < this.maxLimit) && this.customPlanUserSignal.update(prev => prev + 1);
    } else if (flag === this.customPlanGroupAppointmentType) {
      (this.customPlanGroupAppointmentSignal() < this.maxLimit) && this.customPlanGroupAppointmentSignal.update(prev => prev + 1);
    } else if (flag === this.customPlanStoreType) {
      (this.customPlanStoreSignal() < this.maxLimit) && this.customPlanStoreSignal.update(prev => prev + 1);
    } else if (flag === this.customPlanConferenceType) {
      if (this.customPlanConferenceSignal() === 0) {
        this.customPlanConferenceSignal.set(5);
      } else if (this.customPlanConferenceSignal() < this.maxLimit) {
        this.customPlanConferenceSignal.update(prev => prev + 1);
      }
    }
    this.calculateCustomPlanPrice();
  }

  private calculateCustomPlanPrice(): void {
    let preapredCustomPlan: PricingDetails;
    this.availablePlansSignal.set([]);
    this.pricingListSignal().forEach(plan => {
      if (
        plan.max_workers >= this.customPlanUserSignal() &&
        plan.max_store >= this.customPlanStoreSignal() &&
        plan.max_group_appointment >= this.customPlanGroupAppointmentSignal() &&
        plan.max_conference >= this.customPlanConferenceSignal()
      ) {
        this.availablePlansSignal.update(prev => [...prev, plan.id]);
      }
    });
    // here selecting the first available plan as default plan becuase we are showing it with precedence.
    preapredCustomPlan = this.pricingListSignal().find(plan => plan.id === this.availablePlansSignal()[0]);
    this.customSelectedPlanSignal.set(preapredCustomPlan);
    this.updateCustomFinalPlanTotal();
  }

  private updateCustomFinalPlanTotal(): void {
    // if custom selected plan is not set then return
    if (!this.customSelectedPlanSignal()) { return; }
    let customPlanTotal = 0;
    const additionPlanMonthlyYearlyMultiplier = (this._intervalSignal() === this.yearly) ? 1 : 1;
    customPlanTotal = +this.customSelectedPlanSignal()?.[this._currencySignal()?.toLowerCase() || this.eur.toLowerCase()]?.[this._intervalSignal()]?.original_price * additionPlanMonthlyYearlyMultiplier;
    if (this.customSelectedPlanSignal()?.min_workers < this.customPlanUserSignal()) {
      customPlanTotal = customPlanTotal + ((this.customPlanUserSignal() - this.customSelectedPlanSignal()?.min_workers) * this.extraUserCost * additionPlanMonthlyYearlyMultiplier);
    }
    if (this.customSelectedPlanSignal()?.min_store < this.customPlanStoreSignal()) {
      customPlanTotal = customPlanTotal + ((this.customPlanStoreSignal() - this.customSelectedPlanSignal()?.min_store) * this.extraStoreCost * additionPlanMonthlyYearlyMultiplier);
    }
    if (this.customSelectedPlanSignal()?.min_group_appointment < this.customPlanGroupAppointmentSignal()) {
      customPlanTotal = customPlanTotal + ((this.customPlanGroupAppointmentSignal() - this.customSelectedPlanSignal()?.min_group_appointment) * this.extraGroupAppointmentCost * additionPlanMonthlyYearlyMultiplier);
    }
    if (this.customSelectedPlanSignal()?.min_conference < this.customPlanConferenceSignal()) {
      customPlanTotal = customPlanTotal + ((this.customPlanConferenceSignal() - this.customSelectedPlanSignal()?.min_conference) * this.extraConferenceCost * additionPlanMonthlyYearlyMultiplier);
    }
    this.customPlanTotalAmountSignal.set(customPlanTotal);
    this.applyCustomProfileToPricingTable();
  }

  private getCurrentFromIp(): void {
    this.setCurrency(CURRENCY.CHF);
    this.helperService.getCurrentFromIp().subscribe({
      next: ipInfo => {
        const country = ipInfo?.country;
        let detectedCurrency: Currency = CURRENCY.EUR;
        if (country === 'CH' || country === 'LI') {
          detectedCurrency = CURRENCY.CHF;
        } else if (country === 'US') {
          detectedCurrency = CURRENCY.USD;
        } else if (country === 'GB' || country === 'UK') {
          detectedCurrency = CURRENCY.GBP;
        }
        this.setCurrency(detectedCurrency);
      },
      error: () => this.setCurrency(CURRENCY.CHF)
    })
  }

  private setPricingJson(): void {
    let tempPricingList = structuredClone(resolvePricingList());
    tempPricingList = this.filterPricingListBasedOnMinimumSubscription(tempPricingList);
    this.pricingListSignal.set(this.helperService.setupMaxLimiteInPricingList(tempPricingList));
  }

  private filterPricingListBasedOnMinimumSubscription(pricingList: PricingDetails[]): PricingDetails[] {
    if (this._minimumSubscriptionSignal()) {
      const minPrecedence = this.pricingPlanPrecedence[this._minimumSubscriptionSignal() as keyof typeof PRICING_PLAN_PRECEDENCE];
      if (typeof minPrecedence === 'number') {
        pricingList = pricingList.filter(plan => this.pricingPlanPrecedence[plan.id as keyof typeof PRICING_PLAN_PRECEDENCE] >= minPrecedence);
      }
    }
    return pricingList;
  }
}

